import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, UserSquare, Users, Briefcase, Calculator, ClipboardList, ShoppingCart,
  Package, Store, HardHat, ShieldCheck, FileText, Wallet, BarChart3, Bot, Settings,
  Search, Bell, ChevronDown, LogOut, Menu, Building2, CreditCard, Check,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Spinner } from "@/components/common/Common";
import { VoiceCopilot } from "@/components/app/VoiceCopilot";

const BizContext = createContext(null);
export const useBiz = () => useContext(BizContext);

// [label, path, icon, feature-flag(optional)]
const NAV = [
  ["Overview", "/business/app", LayoutDashboard],
  ["Leads", "/business/leads", UserSquare, "crm"],
  ["Clients", "/business/clients", Users, "crm"],
  ["Projects", "/business/projects", Briefcase],
  ["Estimation", "/business/estimation", Calculator, "estimation"],
  ["BOQ", "/business/boq", ClipboardList, "boq"],
  ["Procurement", "/business/procurement", ShoppingCart, "procurement"],
  ["Materials", "/business/materials", Package, "materials"],
  ["Vendors", "/business/vendors", Store, "vendors"],
  ["Workforce", "/business/workforce", HardHat, "workforce"],
  ["Quality", "/business/quality", ShieldCheck, "quality"],
  ["Documents", "/business/documents", FileText, "documents"],
  ["Finance", "/business/finance", Wallet, "finance"],
  ["Reports", "/business/reports", BarChart3, "reports"],
  ["AI Copilot", "/business/ai", Bot, "ai"],
  ["Team", "/business/team", Users, "team"],
  ["Settings", "/business/settings", Settings],
];

export function BusinessShell({ children, title }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(null);
  const [ent, setEnt] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const loadEnt = useCallback(async (oid) => {
    const { data } = await api.get(`/organizations/${oid}/entitlements`);
    setEnt(data);
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/organizations/mine");
      setOrgs(data);
      const oid = user?.active_organization_id || data[0]?.id;
      if (oid) { setOrgId(oid); loadEnt(oid); }
      else { setOrgId("none"); }
    })();
  }, [user, loadEnt]);

  useEffect(() => { api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {}); }, [loc.pathname]);
  useEffect(() => {
    if (!orgId || orgId === "none" || q.length < 2) { setResults(null); return; }
    const t = setTimeout(() => api.get(`/organizations/${orgId}/search`, { params: { q } })
      .then(({ data }) => setResults(data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, orgId]);

  const unread = notifs.filter((n) => !n.read).length;
  const markAll = () => { api.post("/notifications/read-all"); setNotifs(notifs.map((n) => ({ ...n, read: true }))); };

  const switchOrg = async (oid) => {
    await api.post("/auth/account-type", { account_type: "business" }).catch(() => {});
    setOrgId(oid); await loadEnt(oid); nav("/business/app");
  };

  const can = (feature) => !feature || (ent?.features || []).includes(feature);
  const items = NAV.filter(([, , , f]) => can(f));

  const searchGroups = results ? Object.entries(results).filter(([, v]) => v.length) : [];

  const Sidebar = (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center px-5 border-b border-white/10 shrink-0"><Link to="/"><Logo dark /></Link></div>
      {/* Org switcher */}
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid="org-switcher" className="mx-3 mt-3 flex items-center gap-2 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2.5 text-left">
            <div className="h-8 w-8 rounded-md bg-sp-orange flex items-center justify-center shrink-0"><Building2 className="h-4 w-4 text-white" /></div>
            <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-white truncate">{orgs.find((o) => o.id === orgId)?.name || "Workspace"}</div>
              <div className="text-[10px] uppercase tracking-wide text-sp-orange">{ent?.plan || "—"} plan</div></div>
            <ChevronDown className="h-4 w-4 text-white/50" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-1">
          {orgs.map((o) => (
            <button key={o.id} onClick={() => switchOrg(o.id)} data-testid={`org-opt-${o.id}`}
              className="w-full text-left px-2 py-2 text-sm rounded hover:bg-slate-50 flex items-center justify-between">
              {o.name}{o.id === orgId && <Check className="h-4 w-4 text-sp-orange" />}
            </button>
          ))}
          <button onClick={() => nav("/onboarding/business")} className="w-full text-left px-2 py-2 text-sm rounded hover:bg-slate-50 text-sp-orange">+ New organization</button>
        </PopoverContent>
      </Popover>

      <nav className="flex-1 py-3 px-3 space-y-0.5 sp-scroll overflow-y-auto">
        {items.map(([label, to, Icon]) => {
          const active = loc.pathname === to;
          return (
            <Link key={label} to={to} onClick={() => setMobileOpen(false)} data-testid={`biz-nav-${label.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-sp-orange text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
              <Icon className="h-4 w-4" />{label}
            </Link>
          );
        })}
        <Link to="/business/billing" onClick={() => setMobileOpen(false)} data-testid="biz-nav-billing"
          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${loc.pathname === "/business/billing" ? "bg-sp-orange text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
          <CreditCard className="h-4 w-4" />Billing
        </Link>
      </nav>
      <div className="p-3 border-t border-white/10">
        <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/5 w-full"><LogOut className="h-4 w-4" />Sign out</button>
      </div>
    </div>
  );

  const ctx = { orgId, org: orgs.find((o) => o.id === orgId), ent, can, reloadEnt: () => loadEnt(orgId) };

  return (
    <BizContext.Provider value={ctx}>
      <div className="min-h-screen bg-sp-light flex">
        <aside className="hidden lg:flex w-60 bg-sp-navy fixed inset-y-0 z-40">{Sidebar}</aside>
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <aside className="w-64 bg-sp-navy">{Sidebar}</aside>
            <div className="flex-1 bg-black/50" onClick={() => setMobileOpen(false)} />
          </div>
        )}
        <div className="flex-1 lg:ml-60 min-w-0">
          <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-30 flex items-center gap-3 px-4 sm:px-6">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="biz-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects, clients, vendors..." className="pl-9 h-9 bg-slate-50 border-slate-200" />
              {searchGroups.length > 0 && (
                <div className="absolute mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-50 max-h-96 overflow-y-auto" data-testid="biz-search-results">
                  {searchGroups.map(([group, list]) => (
                    <div key={group}><div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50">{group}</div>
                      {list.map((r) => (
                        <button key={r.id} onClick={() => { nav(r.link); setQ(""); }} className="w-full text-left px-4 py-2 hover:bg-slate-50">
                          <div className="text-sm text-sp-navy">{r.title}</div><div className="text-xs text-slate-400">{r.subtitle}</div></button>
                      ))}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1 hidden sm:block text-sm font-medium text-sp-navy truncate">{title}</div>
            <Popover>
              <PopoverTrigger asChild>
                <button className="relative p-2 rounded-lg hover:bg-slate-100" data-testid="notif-bell"><Bell className="h-5 w-5 text-slate-600" />
                  {unread > 0 && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-sp-orange text-[10px] text-white flex items-center justify-center">{unread}</span>}</button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b"><span className="font-semibold text-sm">Notifications</span>{unread > 0 && <button onClick={markAll} className="text-xs text-sp-orange">Mark all read</button>}</div>
                <div className="max-h-80 overflow-y-auto sp-scroll">{notifs.length === 0 ? <div className="p-6 text-center text-sm text-slate-400">No notifications</div> :
                  notifs.slice(0, 12).map((n) => (<div key={n.id} className={`px-4 py-3 border-b last:border-0 ${!n.read ? "bg-sp-orange/5" : ""}`}><div className="text-sm font-medium text-sp-navy">{n.title}</div><div className="text-xs text-slate-500 mt-0.5">{n.message}</div></div>))}</div>
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild><button className="flex items-center gap-2 p-1 rounded-lg hover:bg-slate-100" data-testid="profile-btn">
                <Avatar className="h-8 w-8"><AvatarFallback className="bg-sp-navy text-white text-xs">{(user?.name || "U").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                <ChevronDown className="h-4 w-4 text-slate-400 hidden sm:block" /></button></PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-2">
                <div className="px-2 py-2 border-b mb-1"><div className="text-sm font-medium text-sp-navy">{user?.name}</div><div className="text-xs text-slate-400">{user?.email}</div></div>
                <button onClick={() => nav("/app/home")} className="w-full text-left px-2 py-2 text-sm rounded hover:bg-slate-50">My Home (B2C)</button>
                {user?.role === "admin" && <button onClick={() => nav("/app/admin")} className="w-full text-left px-2 py-2 text-sm rounded hover:bg-slate-50">Platform Admin</button>}
                <button onClick={logout} className="w-full text-left px-2 py-2 text-sm rounded hover:bg-slate-50 text-red-600">Sign out</button>
              </PopoverContent>
            </Popover>
          </header>
          <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
            {orgId === "none" ? <NoOrg /> : !ent ? <Spinner label="Loading workspace..." /> : children}
          </main>
        </div>
        {orgId && orgId !== "none" && can("ai") && <VoiceCopilot orgId={orgId} />}
      </div>
    </BizContext.Provider>
  );
}

function NoOrg() {
  const nav = useNavigate();
  return (
    <div className="max-w-md mx-auto text-center py-20" data-testid="no-org">
      <Building2 className="h-10 w-10 text-sp-orange mx-auto" />
      <h2 className="text-xl font-bold text-sp-navy mt-4">Set up your business</h2>
      <p className="text-sm text-slate-500 mt-1">Create your construction workspace to unlock the operating system.</p>
      <button onClick={() => nav("/onboarding/business")} className="mt-6 bg-sp-orange hover:bg-sp-orangedark text-white rounded-full px-6 py-2.5 text-sm">Create Organization</button>
    </div>
  );
}
